import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#141414]">
      {/* Header */}
      <header className="max-w-[800px] mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-8 h-8 bg-[#7B2FFF] rounded-md flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-bold tracking-tight text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            HighConnect
          </span>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-[#EDEDF0] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </header>

      {/* Content */}
      <main className="max-w-[800px] mx-auto px-6 py-8 pb-20" data-testid="privacy-policy-page">
        <p className="text-xs text-[#71717A] mb-2">Last Updated: May 2026</p>
        <h1
          className="text-3xl sm:text-4xl font-bold text-[#EDEDF0] mb-8"
          style={{ fontFamily: "'Unbounded', sans-serif" }}
        >
          Privacy Policy
        </h1>

        <div className="prose-custom space-y-6 text-sm text-[#A1A1AA] leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Operator
            </h2>
            <p>
              HighConnect is operated by <strong className="text-[#EDEDF0]">Rochelle Weber</strong>, Gold Coast, Queensland, Australia.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              What Data We Collect
            </h2>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Account information (email address, name, password hash)</li>
              <li>GoHighLevel account credentials (API key/OAuth tokens, Location ID)</li>
              <li>Your OpenAI API key (stored securely for message processing)</li>
              <li>Lead message data received via webhook from GoHighLevel</li>
              <li>AI-generated bot responses sent to leads</li>
              <li>Activity and usage logs (message counts, timestamps)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              How We Use Your Data
            </h2>
            <p>
              Your data is used solely to route messages between GoHighLevel and the configured AI model (OpenAI GPT-4o). 
              We process incoming lead messages, generate AI responses using your configured prompt and API key, 
              and send replies back through the GoHighLevel API.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Bring Your Own Key (BYOK)
            </h2>
            <p>
              HighConnect uses a BYOK model. You provide your own OpenAI API key. You are solely responsible 
              for your OpenAI API usage, costs, and compliance with OpenAI's terms of service. HighConnect does 
              not provide or subsidise API credits.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Data Storage and Retention
            </h2>
            <p>
              Lead conversation data is processed in transit and may be temporarily stored for activity logging 
              purposes (conversation history, message counts, and bot activity stats). This data is retained 
              to provide you with conversation logs and analytics within the dashboard.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Third-Party Sharing
            </h2>
            <p>
              Your data is <strong className="text-[#EDEDF0]">not sold or shared with third parties</strong>. 
              Data is only transmitted to GoHighLevel (to send replies) and OpenAI (to generate responses) 
              as part of the core service functionality, using your own credentials and API keys.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Data Deletion
            </h2>
            <p>
              You may request deletion of your data at any time by contacting{' '}
              <a href="mailto:rochelle@fastreply247.com" className="text-[#7B2FFF] hover:text-[#9353FF] underline">
                rochelle@fastreply247.com
              </a>. 
              Upon request, we will delete your account, stored credentials, and all associated conversation data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#EDEDF0] mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Contact
            </h2>
            <p>
              For any privacy-related questions or concerns, contact us at{' '}
              <a href="mailto:rochelle@fastreply247.com" className="text-[#7B2FFF] hover:text-[#9353FF] underline">
                rochelle@fastreply247.com
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#27272A] py-6">
        <div className="max-w-[800px] mx-auto px-6 flex items-center justify-between">
          <span className="text-xs text-[#71717A]">HighConnect</span>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#7B2FFF]">Privacy Policy</span>
            <a href="/terms-of-service" className="text-xs text-[#71717A] hover:text-[#EDEDF0] transition-colors">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
